import { useEffect, useRef, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Box,
  Typography,
} from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { colors } from '@src/styles';
import { monoFontFamily, paginationSX } from '../scriptsStyles';

type ColumnAlign = 'left' | 'center' | 'right';

const MIN_ROW_HEIGHT = 50;

const JUSTIFY: Record<ColumnAlign, string> = { left: 'flex-start', center: 'center', right: 'flex-end' };

interface DataTableProps<T> {
  data: T[];
  columns: Array<{
    label: string;
    key: keyof T | string;
    width?: string | number;
    render?: (row: T) => React.ReactNode;
    icon?: React.ReactNode;
    requestActionDetail?: (row: T) => void;
    sortable?: boolean;
    align?: ColumnAlign;
  }>;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  fillContainer?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onRowClick?: (row: T) => void;
  stretchRows?: boolean;
}

export const DataTable = <T extends { id: string }>({
  data = [],
  columns,
  currentPage: externalPage,
  totalPages: externalTotalPages,
  onPageChange,
  fillContainer = false,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  stretchRows = false,
}: DataTableProps<T>) => {
  const [internalPage, setInternalPage] = useState(1);
  const itemsPerPage = 10;

  const isExternalPagination =
    externalPage !== undefined && externalTotalPages !== undefined && onPageChange !== undefined;

  const page = isExternalPagination ? externalPage : internalPage;
  const paginatedData = isExternalPagination
    ? data
    : data.slice((internalPage - 1) * itemsPerPage, internalPage * itemsPerPage);
  const totalPages = isExternalPagination ? externalTotalPages : Math.ceil(data.length / itemsPerPage);

  const containerRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLTableSectionElement>(null);
  const [rowHeight, setRowHeight] = useState<number | undefined>(undefined);
  const rowCount = paginatedData.length;

  useEffect(() => {
    const container = containerRef.current;
    if (!stretchRows || !container || rowCount === 0) return undefined;
    const updateRowHeight = () => {
      const available = container.clientHeight - (headRef.current?.offsetHeight ?? 0);
      const next = Math.floor(available / rowCount);
      setRowHeight(next > MIN_ROW_HEIGHT ? next : undefined);
    };
    updateRowHeight();
    const observer = new ResizeObserver(updateRowHeight);
    observer.observe(container);
    return () => observer.disconnect();
  }, [stretchRows, rowCount]);

  const handlePageChange = (_: unknown, value: number) => {
    if (isExternalPagination) {
      onPageChange(value);
    } else {
      setInternalPage(value);
    }
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: fillContainer ? 1 : undefined,
          p: 6,
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            border: `1px solid ${colors.brand.line}`,
          }}
        >
          <InboxOutlinedIcon sx={{ fontSize: 24, color: `${colors.brand.textMuted} !important` }} />
        </Box>
        <Typography sx={{ color: colors.brand.textSecondary, fontSize: '13.5px', textAlign: 'center' }}>
          Nenhum item encontrado
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: 0,
        ...(fillContainer && { flex: 1 }),
      }}
    >
      <TableContainer
        ref={containerRef}
        sx={{
          ...(fillContainer ? { flexGrow: 1, minHeight: 0 } : { maxHeight: '60vh' }),
          overflow: 'auto',
        }}
      >
        <Table stickyHeader sx={{ minWidth: 760 }}>
          <TableHead ref={headRef}>
            <TableRow>
              {columns.map((column) => {
                const isSorted = sortBy === column.key;
                const isSortable = column.sortable !== false && onSort;
                const align = column.align ?? 'center';
                return (
                  <TableCell
                    key={String(column.key)}
                    sx={{
                      fontFamily: monoFontFamily,
                      fontSize: '10px',
                      fontWeight: 500,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: isSorted ? colors.primary.yellow : colors.brand.textMuted,
                      borderBottom: `1px solid ${colors.brand.line}`,
                      backgroundColor: colors.brand.surface,
                      textAlign: align,
                      paddingY: 1.25,
                      whiteSpace: 'nowrap',
                      ...(column.width != null && { width: column.width }),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: JUSTIFY[align], gap: 0.5 }}>
                      <Box
                        component={isSortable ? 'button' : 'span'}
                        type={isSortable ? 'button' : undefined}
                        onClick={() => isSortable && onSort(String(column.key))}
                        sx={{
                          all: 'unset',
                          ...(isSortable && {
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { color: colors.primary.white },
                            '&:focus-visible': { outline: `1px solid ${colors.primary.yellow}`, borderRadius: '3px' },
                          }),
                        }}
                      >
                        {column.label}
                      </Box>
                      {isSortable &&
                        isSorted &&
                        (sortOrder === 'asc' ? (
                          <ArrowUpwardIcon sx={{ fontSize: '13px', color: `${colors.primary.yellow} !important` }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: '13px', color: `${colors.primary.yellow} !important` }} />
                        ))}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  height: rowHeight,
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
                  '&:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
                  '& .MuiTableCell-root': {
                    color: colors.primary.white,
                    borderBottom: `1px solid ${colors.brand.line}`,
                    fontSize: '13px',
                    paddingY: 1.25,
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell key={String(column.key)} sx={{ textAlign: column.align ?? 'center' }}>
                    {column.render ? column.render(row) : String(row[column.key as keyof T] ?? '-')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            paddingX: 2,
            paddingY: 1.25,
            flexShrink: 0,
            borderTop: `1px solid ${colors.brand.line}`,
          }}
        >
          <Typography sx={{ fontFamily: monoFontFamily, fontSize: '11px', color: colors.brand.textMuted }}>
            página {page} de {totalPages}
          </Typography>
          <Pagination count={totalPages} page={page} onChange={handlePageChange} size="small" sx={paginationSX} />
        </Box>
      )}
    </Box>
  );
};
